import * as React from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, LayoutDashboard } from "lucide-react";
import { useWedding } from "@/hooks/useWedding";
import { useLanguage } from "@/contexts/LanguageContext";
import { WEDDING_SCOPED_MODULES, weddingModulePath } from "@/lib/constants";
import { LoadingScreen } from "@/components/common/LoadingScreen";
import { cn } from "@/lib/utils";

/** Resolve the URL within the current organization before exposing event modules. */
export function WeddingWorkspace() {
  const { weddingId } = useParams<{ weddingId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { weddings, activeWedding, setActiveWeddingId, isLoading } = useWedding();
  const { t } = useLanguage();
  const wedding = weddings.find((item) => item.id === weddingId);

  React.useEffect(() => {
    if (wedding && wedding.id !== activeWedding?.id) setActiveWeddingId(wedding.id);
  }, [wedding, activeWedding?.id, setActiveWeddingId]);

  if (isLoading) return <LoadingScreen label={t("Loading wedding…")} />;
  if (!wedding) return <Navigate to="/weddings" replace />;
  if (activeWedding?.id !== wedding.id) return <LoadingScreen label={t("Loading wedding…")} />;

  const modules = [
    { label: "Overview", path: "", icon: LayoutDashboard },
    ...WEDDING_SCOPED_MODULES,
  ];

  return <div className="space-y-6">
    <section className="rounded-xl border border-border bg-surface p-4 print:hidden">
      <Link to="/weddings" className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />{t("All weddings")}
      </Link>
      <p className="mb-4 truncate font-display text-lg font-semibold">{wedding.coupleNames}</p>
      <select aria-label={t("Wedding navigation")} value={pathname} onChange={(event) => navigate(event.target.value)} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm sm:hidden">
        {modules.map((module) => <option key={module.path} value={weddingModulePath(wedding.id, module.path)}>{t(module.label)}</option>)}
      </select>
      <nav aria-label={t("Wedding navigation")} className="hidden flex-wrap gap-1.5 sm:flex">
        {modules.map((module) => <NavLink key={module.path} to={weddingModulePath(wedding.id, module.path)} end
          className={({ isActive }) => cn("inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors", isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground")}>
          <module.icon className="h-4 w-4 shrink-0" aria-hidden="true" />{t(module.label)}
        </NavLink>)}
      </nav>
    </section>
    <Outlet key={wedding.id} />
  </div>;
}

/** Keep old bookmarks working while moving modules into their event workspace. */
export function WeddingModuleRedirect({ modulePath }: { modulePath: string }) {
  const { activeWedding, isLoading } = useWedding();
  if (isLoading) return <LoadingScreen />;
  return <Navigate to={activeWedding ? weddingModulePath(activeWedding.id, modulePath) : "/weddings"} replace />;
}
